import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';

interface CompanyContextType {
  memoryEnabled: boolean;
  setMemoryEnabled: (enabled: boolean) => void;
  isOpsInternalUser: boolean;
  loading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [memoryEnabled, setMemoryEnabled] = useState<boolean>(false);
  const [isOpsInternalUser, setIsOpsInternalUser] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchUserDetails = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/user-details', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsOpsInternalUser(response.data.isOpsInternalUser || false);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  return (
    <CompanyContext.Provider
      value={{ memoryEnabled, setMemoryEnabled, isOpsInternalUser, loading }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
